/**
 * Spot Market Rate Engine — Broker-Style Comps
 *
 * Calculates a realistic spot-market rate for a lane using ONLY live posted
 * loads that match how brokers actually evaluate lanes.
 *
 * Filtering:
 *  - FTL only
 *  - True 1-pick / 1-drop (verified via load_stops count, not just load_type)
 *  - Same equipment type
 *  - Origin within DH-O radius, destination within DH-D radius
 *
 * Outlier protection: IQR box-plot method on RPM values.
 * Minimum 3 valid comps required; otherwise returns null.
 *
 * 24h Rolling Trend: compares current live RPM against comps created 24-48h ago
 * for the same lane/equipment. This is an approximate proxy, not exact history.
 */

import { supabase } from '@/integrations/supabase/client';
import { boundingBox, haversineDistance, EQUIPMENT_FAMILIES } from '@/lib/loadSearch';
import { NATIONAL_BASELINE_RPM } from '@/lib/rateEngine/baselineRpm';

/* ─── Types ─── */

export interface SpotRateInput {
  originLat: number;
  originLon: number;
  destLat: number;
  destLon: number;
  laneMiles: number;
  equipmentType: string; // DB enum value, e.g. 'van', 'reefer'
  /** Load IDs to exclude from comp pool (e.g. user's own loads) */
  excludeLoadIds?: string[];
  /** Optional city/state — enables L4 (daily baseline) fallback. Skipped silently if missing. */
  originCity?: string;
  originState?: string;
  destCity?: string;
  destState?: string;
}

export interface SpotRateComp {
  id: string;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
  rpm: number;
  rateAmount: number;
  totalMiles: number;
  dhOrigin: number; // deadhead miles from search origin
  dhDest: number;   // deadhead miles from search destination
}

export type SpotConfidence = 'high' | 'medium' | 'low';

export type SpotTier = 1 | 2 | 3 | 4 | 5;

export type TrendDirection = 'up' | 'down' | 'neutral';

export interface SpotRateTrend {
  direction: TrendDirection;
  changePercent: number;
  baselineRpm: number;
}

export interface SpotRateResult {
  averageRpm: number;
  suggestedRate: number;
  compCount: number;
  comps: SpotRateComp[];
  /** 1=live ≤150mi · 2=live ≤250mi · 3=14-day historical · 4=daily baseline · 5=national RPM fallback */
  tier: SpotTier;
  /** Short UI label, e.g. "Live · 5 comps", "14-day avg · 12 comps", "Daily baseline · May 24", "Market rate" */
  tierLabel: string;
  /** YYYY-MM-DD baseline date when tier=4, else null */
  baselineDate: string | null;
  marketRate: number;
  marketRpm: number;
  suggestedPostRate: number;
  negotiationMarkupPercent: number;
  confidence: SpotConfidence;
  confidencePercent: number;
  trend: SpotRateTrend | null;
}

export interface SpotRateOptions {
  /**
   * FUTURE: When true, allow fallback to recent historical comps
   * (e.g. last 24h). Not implemented in Phase 1.
   */
  allowHistorical?: boolean;
  /** Skip trend calculation (e.g. for batch where trend is computed separately) */
  skipTrend?: boolean;
}

/* ─── Constants ─── */

const TIER_1_RADIUS = 150; // miles
const TIER_2_RADIUS = 250; // miles
const MIN_COMPS = 3;
const MAX_COMPS = 5;
const TREND_THRESHOLD_PERCENT = 3;

/* ─── Core ─── */

export async function computeSpotRate(
  input: SpotRateInput,
  options?: SpotRateOptions,
): Promise<SpotRateResult | null> {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const result = await computeSpotRateInner(input, options);
  try {
    // Lazy import to avoid a cycle (telemetry imports types from this file).
    const { emitRateGuardTelemetry } = await import('@/lib/spotRateTelemetry');
    const laneKey = (input.originCity && input.originState && input.destCity && input.destState)
      ? makeLaneKey(input.originCity, input.originState, input.destCity, input.destState, input.equipmentType)
      : `geo:${input.originLat.toFixed(2)},${input.originLon.toFixed(2)}>${input.destLat.toFixed(2)},${input.destLon.toFixed(2)}`;
    emitRateGuardTelemetry({
      tier_resolved: result?.tier ?? null,
      execution_ms: Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now())) - t0),
      comp_count: result?.compCount ?? 0,
      lane_key: laneKey,
      equipment: input.equipmentType,
      ts: new Date().toISOString(),
    });
  } catch { /* telemetry must never break the hot path */ }
  return result;
}

async function computeSpotRateInner(
  input: SpotRateInput,
  options?: SpotRateOptions,
): Promise<SpotRateResult | null> {
  const excludeIds = input.excludeLoadIds;

  // Tier 1 — live ≤150mi
  const t1 = await fetchAndScore(input, TIER_1_RADIUS, excludeIds, 'live');
  if (t1.length >= MIN_COMPS) return buildResult(t1, input, 1, options);

  // Tier 2 — live ≤250mi
  const t2 = await fetchAndScore(input, TIER_2_RADIUS, excludeIds, 'live');
  if (t2.length >= MIN_COMPS) return buildResult(t2, input, 2, options);

  // L3 — 14-day historical (loads relaxed + archive UNION, 250mi)
  const t3 = await fetchAndScore(input, TIER_2_RADIUS, excludeIds, 'historical');
  if (t3.length >= MIN_COMPS) return buildResult(t3, input, 3, options);

  // L4 — Daily baseline (requires city/state in input)
  const t4 = await fetchBaselineFallback(input);
  if (t4) return t4;

  // L5 — National baseline RPM × lane miles (silent fallback)
  const t5 = buildNationalBaselineFallback(input);
  if (t5) return t5;

  // Insufficient data
  return null;
}

/* ─── L5 — National baseline RPM fallback (silent) ─── */

function resolveBaselineFamily(equipmentType: string): 'van' | 'reefer' | 'flatbed' | null {
  const eq = equipmentType?.toLowerCase();
  if (!eq) return null;
  if (eq in NATIONAL_BASELINE_RPM) return eq as 'van' | 'reefer' | 'flatbed';
  for (const family of ['van', 'reefer', 'flatbed'] as const) {
    if (EQUIPMENT_FAMILIES[family]?.includes(eq)) return family;
  }
  return null;
}

function buildNationalBaselineFallback(input: SpotRateInput): SpotRateResult | null {
  const family = resolveBaselineFamily(input.equipmentType);
  if (!family) return null;
  const rpm = NATIONAL_BASELINE_RPM[family];
  if (!rpm || input.laneMiles <= 0) return null;

  const marketRate = Math.round(rpm * input.laneMiles);
  const marketRpm = Math.round(rpm * 100) / 100;
  const NEGOTIATION_MARKUP = 9;

  return {
    averageRpm: marketRpm,
    suggestedRate: marketRate,
    compCount: 0,
    comps: [],
    tier: 5,
    tierLabel: 'Market rate',
    baselineDate: null,
    marketRate,
    marketRpm,
    suggestedPostRate: Math.round(marketRate * (1 + NEGOTIATION_MARKUP / 100)),
    negotiationMarkupPercent: NEGOTIATION_MARKUP,
    confidence: 'high',
    confidencePercent: 85,
    trend: null,
  };
}

/* ─── DB fetch + filtering pipeline ─── */

interface RawComp {
  id: string;
  origin_city: string;
  origin_state: string;
  origin_latitude: number;
  origin_longitude: number;
  destination_city: string;
  destination_state: string;
  destination_latitude: number;
  destination_longitude: number;
  rate_amount: number;
  total_miles: number;
}

type FetchMode = 'live' | 'historical';

async function fetchAndScore(
  input: SpotRateInput,
  radius: number,
  excludeLoadIds?: string[],
  mode: FetchMode = 'live',
): Promise<SpotRateComp[]> {
  const { originLat, originLon, destLat, destLon, equipmentType } = input;

  // Bounding boxes for origin + destination
  const oBB = boundingBox(originLat, originLon, radius);
  const dBB = boundingBox(destLat, destLon, radius);

  const COLS =
    'id, origin_city, origin_state, origin_latitude, origin_longitude, ' +
    'destination_city, destination_state, destination_latitude, destination_longitude, ' +
    'rate_amount, total_miles';

  const applyCommonFilters = (q: any) =>
    q
      .eq('load_type', 'FTL')
      .eq('equipment_type', equipmentType as any)
      .gt('rate_amount', 0)
      .gt('total_miles', 0)
      .gte('origin_latitude', oBB.minLat).lte('origin_latitude', oBB.maxLat)
      .gte('origin_longitude', oBB.minLon).lte('origin_longitude', oBB.maxLon)
      .gte('destination_latitude', dBB.minLat).lte('destination_latitude', dBB.maxLat)
      .gte('destination_longitude', dBB.minLon).lte('destination_longitude', dBB.maxLon);

  let rawRows: RawComp[] = [];

  if (mode === 'live') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cutoff = today.toISOString().split('T')[0];

    let query: any = applyCommonFilters(
      supabase.from('loads').select(COLS)
        .eq('is_public', true)
        .eq('status', 'posted')
        .gte('pickup_date', cutoff),
    );
    if (excludeLoadIds?.length) {
      query = query.not('id', 'in', `(${excludeLoadIds.join(',')})`);
    }
    const { data, error } = await query;
    if (error || !data?.length) return [];
    rawRows = data as unknown as RawComp[];
  } else {
    // L3 — historical: relaxed loads + archive UNION (JS merge), 14-day window
    const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();

    let liveQ: any = applyCommonFilters(
      supabase.from('loads').select(COLS)
        .in('status', ['posted', 'expired'])
        .gte('created_at', fourteenDaysAgo),
    );
    if (excludeLoadIds?.length) {
      liveQ = liveQ.not('id', 'in', `(${excludeLoadIds.join(',')})`);
    }

    let archQ: any = applyCommonFilters(
      supabase.from('loads_archive').select(COLS)
        .gte('created_at', fourteenDaysAgo),
    );
    if (excludeLoadIds?.length) {
      archQ = archQ.not('id', 'in', `(${excludeLoadIds.join(',')})`);
    }

    const [liveRes, archRes] = await Promise.all([liveQ, archQ]);
    const seen = new Set<string>();
    const merged: RawComp[] = [];
    for (const row of [...(liveRes?.data ?? []), ...(archRes?.data ?? [])] as RawComp[]) {
      if (!row?.id || seen.has(row.id)) continue;
      seen.add(row.id);
      merged.push(row);
    }
    if (!merged.length) return [];
    rawRows = merged;
  }

  // True 1p1d check (only meaningful for live rows; archived rows won't have stops — keep them)
  const loadIds = rawRows.map((l) => l.id);
  const onePickOneDrop = await getOnePickOneDropIds(loadIds);
  const candidates = rawRows.filter((l) => onePickOneDrop.has(l.id));

  const scored: SpotRateComp[] = [];
  for (const l of candidates) {
    const dhO = haversineDistance(originLat, originLon, l.origin_latitude, l.origin_longitude);
    const dhD = haversineDistance(destLat, destLon, l.destination_latitude, l.destination_longitude);
    if (dhO > radius || dhD > radius) continue;

    scored.push({
      id: l.id,
      originCity: l.origin_city,
      originState: l.origin_state,
      destCity: l.destination_city,
      destState: l.destination_state,
      rpm: l.rate_amount / l.total_miles,
      rateAmount: l.rate_amount,
      totalMiles: l.total_miles,
      dhOrigin: Math.round(dhO),
      dhDest: Math.round(dhD),
    });
  }

  if (scored.length < MIN_COMPS) return scored.length > 0 ? scored : [];

  const cleaned = removeOutliers(scored);
  cleaned.sort((a, b) => (a.dhOrigin + a.dhDest) - (b.dhOrigin + b.dhDest));
  return cleaned.slice(0, MAX_COMPS);
}

/* ─── L4 — Daily baseline fallback ─── */

async function fetchBaselineFallback(
  input: SpotRateInput,
): Promise<SpotRateResult | null> {
  const { originCity, originState, destCity, destState, equipmentType, laneMiles } = input;
  if (!originCity || !originState || !destCity || !destState) return null;

  const laneKey = makeLaneKey(originCity, originState, destCity, destState, equipmentType);
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000)
    .toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('lane_daily_baselines')
    .select('baseline_rpm, baseline_date')
    .eq('lane_key', laneKey)
    .gte('baseline_date', sevenDaysAgo)
    .order('baseline_date', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  const baselineRpm = Number(data.baseline_rpm);
  if (!baselineRpm || baselineRpm <= 0) return null;

  const marketRate = Math.round(baselineRpm * laneMiles);
  const NEGOTIATION_MARKUP = 9;

  return {
    averageRpm: baselineRpm,
    suggestedRate: marketRate,
    compCount: 0,
    comps: [],
    tier: 4,
    tierLabel: `Daily baseline · ${formatShortDate(data.baseline_date as string)}`,
    baselineDate: data.baseline_date as string,
    marketRate,
    marketRpm: Math.round(baselineRpm * 100) / 100,
    suggestedPostRate: Math.round(marketRate * (1 + NEGOTIATION_MARKUP / 100)),
    negotiationMarkupPercent: NEGOTIATION_MARKUP,
    confidence: 'low',
    confidencePercent: 55,
    trend: null,
  };
}

function formatShortDate(yyyyMmDd: string): string {
  // Local-noon parse to avoid UTC shift (matches src/lib/dateOnly.ts pattern)
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}


/* ─── Daily Baseline Lookup ─── */

/** Normalize city name for lane key (matches edge function logic) */
function normalizeCityName(city: string): string {
  return city.trim().toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ');
}

/** Build a stable market lane key: normalized origin/dest city+state + equipment */
export function makeLaneKey(
  originCity: string,
  originState: string,
  destCity: string,
  destState: string,
  equipmentType: string,
): string {
  const oCity = normalizeCityName(originCity);
  const oState = originState.toUpperCase().trim();
  const dCity = normalizeCityName(destCity);
  const dState = destState.toUpperCase().trim();
  return `${oCity},${oState}|${dCity},${dState}|${equipmentType}`;
}

/** Get today's date string in EST (UTC-5) */
function getTodayEST(): string {
  const now = new Date();
  const estOffset = -5 * 60;
  const estMs = now.getTime() + estOffset * 60 * 1000;
  const estDate = new Date(estMs);
  return estDate.toISOString().split('T')[0];
}

/**
 * Fetch today's daily baseline RPM for a lane key.
 * Returns null if no baseline exists (insufficient comps at 6AM EST).
 */
export async function fetchDailyBaseline(
  laneKey: string,
): Promise<number | null> {
  const todayEST = getTodayEST();
  const { data, error } = await supabase
    .from('lane_daily_baselines')
    .select('baseline_rpm')
    .eq('lane_key', laneKey)
    .eq('baseline_date', todayEST)
    .maybeSingle();

  if (error || !data) return null;
  return Number(data.baseline_rpm);
}

/**
 * Compute trend from current RPM vs daily baseline RPM.
 * Exported for use in batch trend calculations.
 */
export function computeTrend(
  currentRpm: number,
  baselineRpm: number | null,
): SpotRateTrend | null {
  if (baselineRpm === null || baselineRpm <= 0) return null;

  const changePercent = ((currentRpm - baselineRpm) / baselineRpm) * 100;
  const rounded = Math.round(changePercent * 10) / 10;

  let direction: TrendDirection = 'neutral';
  if (changePercent > TREND_THRESHOLD_PERCENT) direction = 'up';
  else if (changePercent < -TREND_THRESHOLD_PERCENT) direction = 'down';

  return {
    direction,
    changePercent: rounded,
    baselineRpm: Math.round(baselineRpm * 100) / 100,
  };
}

/* ─── True 1-pick / 1-drop verification via load_stops ─── */

async function getOnePickOneDropIds(loadIds: string[]): Promise<Set<string>> {
  if (!loadIds.length) return new Set();

  const { data: stops, error } = await supabase
    .from('load_stops')
    .select('load_id, stop_type')
    .in('load_id', loadIds);

  if (error) {
    console.error('[spotRateEngine] Error querying load_stops:', error);
    return new Set(loadIds);
  }

  const pickCounts = new Map<string, number>();
  const dropCounts = new Map<string, number>();
  const loadsWithStops = new Set<string>();

  for (const s of stops || []) {
    loadsWithStops.add(s.load_id);
    if (s.stop_type === 'pickup') {
      pickCounts.set(s.load_id, (pickCounts.get(s.load_id) || 0) + 1);
    } else if (s.stop_type === 'delivery') {
      dropCounts.set(s.load_id, (dropCounts.get(s.load_id) || 0) + 1);
    }
  }

  const result = new Set<string>();
  for (const id of loadIds) {
    if (!loadsWithStops.has(id)) {
      result.add(id);
    } else {
      const picks = pickCounts.get(id) || 0;
      const drops = dropCounts.get(id) || 0;
      if (picks === 1 && drops === 1) {
        result.add(id);
      }
    }
  }

  return result;
}

/* ─── IQR Outlier Removal ─── */

function removeOutliers(comps: SpotRateComp[]): SpotRateComp[] {
  if (comps.length < 4) return comps;

  const rpms = comps.map(c => c.rpm).sort((a, b) => a - b);
  const q1 = median(rpms.slice(0, Math.floor(rpms.length / 2)));
  const q3 = median(rpms.slice(Math.ceil(rpms.length / 2)));
  const iqr = q3 - q1;
  const lo = q1 - 1.5 * iqr;
  const hi = q3 + 1.5 * iqr;

  return comps.filter(c => c.rpm >= lo && c.rpm <= hi);
}

function median(sorted: number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/* ─── Build final result ─── */

async function buildResult(
  comps: SpotRateComp[],
  input: SpotRateInput,
  tier: SpotTier,
  _options?: SpotRateOptions,
): Promise<SpotRateResult> {
  const avgRpm = comps.reduce((s, c) => s + c.rpm, 0) / comps.length;
  const marketRate = Math.round(avgRpm * input.laneMiles);
  const marketRpm = Math.round(avgRpm * 100) / 100;

  let confidence: SpotConfidence;
  let confidencePercent: number;
  let tierLabel: string;
  if (tier === 1) {
    confidence = comps.length >= 4 ? 'high' : 'medium';
    confidencePercent = Math.min(98, 70 + comps.length * 6);
    tierLabel = `Live · ${comps.length} comps`;
  } else if (tier === 2) {
    confidence = 'medium';
    confidencePercent = Math.min(94, 70 + comps.length * 4);
    tierLabel = `Live · ${comps.length} comps · 250mi`;
  } else {
    // tier === 3 (historical)
    confidence = 'medium';
    confidencePercent = Math.min(80, 55 + comps.length * 3);
    tierLabel = `14-day avg · ${comps.length} comps`;
  }

  const NEGOTIATION_MARKUP = 9;

  return {
    averageRpm: marketRpm,
    suggestedRate: marketRate,
    compCount: comps.length,
    comps,
    tier,
    tierLabel,
    baselineDate: null,
    marketRate,
    marketRpm,
    suggestedPostRate: Math.round(marketRate * (1 + NEGOTIATION_MARKUP / 100)),
    negotiationMarkupPercent: NEGOTIATION_MARKUP,
    confidence,
    confidencePercent,
    trend: null,
  };
}
